import React, { useRef, useState, useMemo } from 'react';
import { getUserTimezone } from '../../../utils/timezoneUtils';
import { useNavigate } from 'react-router-dom';
import '../../../utils/agGridSetup';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';
import { ChevronLeftIcon, ChevronRightIcon, Eye } from 'lucide-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../../styles/pages/DeviceTableView.css';
import '../../../styles/pages/alerts.css';
import '../../../styles/pages/help-support.css';

import { TicketDto } from '../../../services/ticketApi';
import { options } from '../../../utils/helperFunctions';

interface ContactUsTabProps {
  tickets: TicketDto[];
}

const statusClass: Record<string, string> = {
  'In Progress': 'hs-status-inprogress',
  'Open':        'hs-status-open',
  'Resolved':    'hs-status-resolved',
  'Closed':      'hs-status-closed',
};

const dotClass: Record<string, string> = {
  High: 'hs-dot-high', Medium: 'hs-dot-medium', Low: 'hs-dot-low',
};

const ContactUsTab: React.FC<ContactUsTabProps> = ({ tickets }) => {
  const navigate = useNavigate();
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timeZone = userPreferences.timezone || getUserTimezone();
  const gridRef = useRef<AgGridReact>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const goToPage = (page: number) => {
    if (gridRef.current?.api) {
      gridRef.current.api.paginationGoToPage(page);
      setCurrentPage(page);
    }
  };

  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Ticket Id',
      field: 'ticketId',
      minWidth: 110, flex: 0.7,
      sortable: true, filter: false, resizable: false,
    },
    {
      headerName: 'Subject',
      field: 'ticketSubject',
      minWidth: 200, flex: 2,
      sortable: true, filter: false, resizable: false,
    },
    {
      headerName: 'Priority',
      field: 'ticketPriority',
      minWidth: 120, flex: 0.9,
      sortable: true, filter: false, resizable: false,
      cellRenderer: (params: any) => (
        <div className="hs-priority">
          <div className={`hs-priority-dot ${dotClass[params.value]}`} />
          <span>{params.value}</span>
        </div>
      ),
    },
    {
      headerName: 'Status',
      field: 'ticketStatus',
      minWidth: 140, flex: 1,
      sortable: true, filter: false, resizable: false,
      cellRenderer: (params: any) => (
        <span className={`hs-status-badge ${statusClass[params.value] || 'hs-status-open'}`}>
          {params.value}
        </span>
      ),
    },
    {
      headerName: 'Created Date',
      field: 'ticketCreatedAt',
      minWidth: 190, flex: 1.4,
      sortable: true, filter: false, resizable: false,
      valueFormatter: (params: any) =>
        params.value ? new Date(params.value).toLocaleString('en-US', {timeZone: timeZone, ...options}) : '',
    },
    {
      headerName: '',
      field: 'actions',
      minWidth: 60, width: 60,
      sortable: false, filter: false, resizable: false,
      cellRenderer: (params: any) => (
        <button
          className="alerts-action-button"
          onClick={() => navigate(`/help-support/${params.data.ticketId}`)}
          title="View Ticket"
        >
          <Eye size={16} />
        </button>
      ),
    },
  ], [navigate]);

  const gridOptions: GridOptions = useMemo(() => ({
    defaultColDef: { resizable: false, sortable: true, filter: false },
    rowData: tickets,
    columnDefs,
    pagination: true,
    paginationPageSize: 10,
    suppressPaginationPanel: true,
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    suppressRowDeselection: true,
    suppressDragLeaveHidesColumns: true,
    domLayout: 'normal',
    rowHeight: 60,
    headerHeight: 70,
    overlayNoRowsTemplate: '<span class="custom-no-rows">No tickets found</span>',
    icons: {
      sortAscending: '<span style="font-size:12px;">▲</span>',
      sortDescending: '<span style="font-size:12px;">▼</span>',
    },
  }), [columnDefs, tickets]);

  return (
    <>
      {/* Contact cards */}
      <div className="hs-contact-row">
        <div className="hs-contact-card hs-contact-email">
          <div className="hs-contact-icon"><img src={`${process.env.PUBLIC_URL}/HelpSupportIcons/EmailSupport.svg`} /></div>
          <div>
            <p className="hs-contact-title">Email Support</p>
            <p className="hs-contact-value">Support@smarteco.ae</p>
            <p className="hs-contact-sub">Response within 24hours Mon–Sat</p>
          </div>
        </div>
        <div className="hs-contact-card hs-contact-phone">
          <div className="hs-contact-icon"><img src={`${process.env.PUBLIC_URL}/HelpSupportIcons/CallSupport.svg`} /></div>
          <div>
            <p className="hs-contact-title">Phone Support</p>
            <p className="hs-contact-value">+971 2 000 0000</p>
            <p className="hs-contact-sub">Available Sun–Thu, 9:00 AM – 6:00 PM GST</p>
          </div>
        </div>
        <div className="hs-contact-card hs-contact-whatsapp">
          <div className="hs-contact-icon"><img src={`${process.env.PUBLIC_URL}/HelpSupportIcons/ChatSupport.svg`} /></div>
          <div>
            <p className="hs-contact-title">WhatsApp Support</p>
            <p className="hs-contact-value">+971 50 000 0000</p>
            <p className="hs-contact-sub">Quick replies, typically within 2 hours</p>
          </div>
        </div>
      </div>

      {/* Ticket list */}
      <div className="hs-table-section">
        <p className="na-section-label">TICKET LIST</p>
        <div className="ag-theme-alpine w-full flex flex-col flex-1">
          <AgGridReact
            ref={gridRef}
            {...gridOptions}
            className="device-table-grid"
            onPaginationChanged={() => {
              if (gridRef.current?.api) {
                setCurrentPage(gridRef.current.api.paginationGetCurrentPage());
                setTotalPages(gridRef.current.api.paginationGetTotalPages());
              }
            }}
          />
          {totalPages > 1 && (
            <div className="pagination-container h-auto">
              <button disabled={currentPage <= 0} className="pagination-button pagination-previous" onClick={() => goToPage(currentPage - 1)}>
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} onClick={() => goToPage(i)} className={`pagination-button ${currentPage === i ? 'active' : ''}`}>
                  {i + 1}
                </button>
              ))}
              <button disabled={currentPage === totalPages - 1} className="pagination-button pagination-next" onClick={() => goToPage(currentPage + 1)}>
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactUsTab;
